import React, { Fragment, useState, useCallback } from 'react';
import { Input, Button } from 'reactstrap';
import { CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import { Copy, RotateCcw } from 'react-feather';

/**
 * TextPreviewModal — Paste a raw text string (as it exists in a JSON payload or database)
 * and see how different destination systems would render it.
 *
 * The source is ALWAYS raw text. The tabs show destination rendering:
 * - "As HTML" — how a system that interprets HTML tags would display it
 * - "As CWM" — how ConnectWise Manage renders its pseudo-markdown (**bold**, *italic*, etc.)
 * - "As Plain Text" — how a plain-text-only system would display it (strip all formatting)
 * - "Raw" — the exact string as-is, no transformation
 */

const DESTINATION_TABS = [
    { key: 'html', label: 'As HTML' },
    { key: 'cwm', label: 'As CWM' },
    { key: 'plain', label: 'As Plain Text' },
    { key: 'raw', label: 'Raw' },
];

/**
 * Normalize the raw input — convert literal escape sequences (\n, \t, \r)
 * that appear as two characters in a pasted JSON string into actual control characters.
 */
const normalizeInput = (text) => {
    if (!text) return '';
    // Convert literal \n, \r\n, \t sequences (from JSON payloads) to real chars
    let normalized = text.replace(/\\r\\n/g, '\n');
    normalized = normalized.replace(/\\n/g, '\n');
    normalized = normalized.replace(/\\t/g, '\t');
    normalized = normalized.replace(/\\r/g, '\r');
    return normalized;
};

/**
 * Render as HTML destination — the destination system interprets this as HTML.
 * We normalize \n escape sequences to real newlines, then render.
 * Browsers treat bare \n as whitespace in HTML — it won't line-break unless
 * the string already contains <br> or block elements.
 */
const renderAsHtml = (text) => {
    if (!text) return '';
    const normalized = normalizeInput(text);
    return normalized;
};

/**
 * Render as CWM destination — ConnectWise Manage interprets:
 * **bold**, *italic*, ~~strikethrough~~, [url=...]text[/url], newlines as line breaks.
 */
const renderAsCwm = (text) => {
    if (!text) return '';
    let html = normalizeInput(text);
    // Escape HTML so we control rendering
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // CWM BBCode tags (some systems use these)
    html = html.replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>');
    html = html.replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>');
    html = html.replace(/\[u\](.*?)\[\/u\]/gi, '<u>$1</u>');
    html = html.replace(/\[s\](.*?)\[\/s\]/gi, '<s>$1</s>');
    html = html.replace(/\[url=(.*?)\](.*?)\[\/url\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#0d6efd">$2</a>');
    html = html.replace(/\[url\](.*?)\[\/url\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:#0d6efd">$1</a>');
    // CWM markdown-style: **bold**, *italic*, ~~strike~~
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
    html = html.replace(/~~(.+?)~~/g, '<s>$1</s>');
    // Horizontal rules
    html = html.replace(/^-{3,}$/gm, '<hr>');
    // Newlines to <br>
    html = html.replace(/\r?\n/g, '<br>');
    return html;
};

/**
 * Render as plain text destination — strip all formatting markers, just show the text.
 */
const renderAsPlainText = (text) => {
    if (!text) return '';
    let plain = normalizeInput(text);
    // Strip HTML tags
    plain = plain.replace(/<br\s*\/?>/gi, '\n');
    plain = plain.replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n');
    plain = plain.replace(/<[^>]+>/g, '');
    // Strip CWM BBCode
    plain = plain.replace(/\[b\](.*?)\[\/b\]/gi, '$1');
    plain = plain.replace(/\[i\](.*?)\[\/i\]/gi, '$1');
    plain = plain.replace(/\[u\](.*?)\[\/u\]/gi, '$1');
    plain = plain.replace(/\[s\](.*?)\[\/s\]/gi, '$1');
    plain = plain.replace(/\[url=(.*?)\](.*?)\[\/url\]/gi, '$2');
    plain = plain.replace(/\[url\](.*?)\[\/url\]/gi, '$1');
    plain = plain.replace(/\[color=(.*?)\](.*?)\[\/color\]/gi, '$2');
    plain = plain.replace(/\[size=(.*?)\](.*?)\[\/size\]/gi, '$2');
    // Strip markdown-style markers
    plain = plain.replace(/\*\*(.+?)\*\*/g, '$1');
    plain = plain.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '$1');
    plain = plain.replace(/~~(.+?)~~/g, '$1');
    // Decode entities
    plain = plain.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    plain = plain.replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"').replace(/&#39;/g, "'");
    // Collapse excessive blank lines
    plain = plain.replace(/\n{3,}/g, '\n\n');
    return plain.trim();
};

const TextPreviewModal = ({ trigger }) => {

    const [modal, setModal] = useState(false);
    const [inputText, setInputText] = useState('');
    const [activeTab, setActiveTab] = useState('cwm');

    const openModal = () => setModal(true);

    const handleClear = () => {
        setInputText('');
    };

    /**
     * Render the preview based on the selected destination tab.
     */
    const getPreviewContent = useCallback(() => {
        if (!inputText.trim()) {
            return <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>Paste your raw text string above to preview how destination systems will render it</p>;
        }

        switch (activeTab) {
            case 'html':
                return <div dangerouslySetInnerHTML={{ __html: renderAsHtml(inputText) }} />;
            case 'cwm':
                return <div dangerouslySetInnerHTML={{ __html: renderAsCwm(inputText) }} />;
            case 'plain':
                return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'inherit', fontSize: 13 }}>{renderAsPlainText(inputText)}</pre>;
            case 'raw':
                return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, fontFamily: 'monospace', fontSize: 11 }}>{inputText}</pre>;
            default:
                return null;
        }
    }, [inputText, activeTab]);

    /**
     * Get copyable output for the active tab.
     */
    const getCopyableOutput = () => {
        if (!inputText.trim()) return '';
        switch (activeTab) {
            case 'html': return renderAsHtml(inputText);
            case 'cwm': return renderAsCwm(inputText);
            case 'plain': return renderAsPlainText(inputText);
            case 'raw': return inputText;
            default: return inputText;
        }
    };

    const modalBody = (
        <div>
            <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                Paste the raw text string (from a JSON payload, DB record, or API response) to see how each destination system would render it.
            </p>

            {/* Input textarea */}
            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600 }}>Source String</span>
                <Button size='sm' color='light' onClick={handleClear} title='Clear'>
                    <RotateCcw size={12} />
                </Button>
            </div>
            <Input
                type='textarea'
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder='Paste raw text here... e.g. **Title**\nLine 1\nLine 2'
                style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 120, marginBottom: 16, resize: 'vertical' }}
            />

            {/* Destination format tabs */}
            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                <div className='d-flex align-items-center gap-2'>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>Destination renders as:</span>
                    <div className='d-flex gap-1'>
                        {DESTINATION_TABS.map(tab => (
                            <Button key={tab.key} size='sm'
                                color={activeTab === tab.key ? 'dark' : 'light'}
                                style={{ fontSize: 11, padding: '4px 12px' }}
                                onClick={() => setActiveTab(tab.key)}>
                                {tab.label}
                            </Button>
                        ))}
                    </div>
                </div>
                {inputText.trim() && (
                    <Copy size={14} style={{ cursor: 'pointer', opacity: 0.5 }} title='Copy output'
                        onClick={() => handleCopy(getCopyableOutput())} />
                )}
            </div>

            {/* Preview area */}
            <div style={{
                border: '1px solid rgba(128,128,128,0.2)',
                borderRadius: 4,
                padding: 16,
                minHeight: 100,
                maxHeight: 300,
                overflowY: 'auto',
                background: 'rgba(128,128,128,0.02)',
                fontSize: 13,
                lineHeight: 1.6,
            }}>
                {getPreviewContent()}
            </div>
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='Text Format Preview' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
            </Fragment>
        );
    }

    return (
        <CommonModal isOpen={modal} title='Text Format Preview' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
            {modalBody}
        </CommonModal>
    );
};

export default TextPreviewModal;
